// The iPhone app's notifications (Poulpe-627). The shell (ios/) reads the
// APNs token and the person's tap natively, and hands them to the page as
// events, only to the document it loaded (its `data-session-document`),
// once that document's session is confirmed:
// - `poulpe-native-push` with `{token, environment, app}`: the page registers
//   this session's phone through the API; with `{forget: true}`, the person
//   turned notifications off, and it forgets it;
// - `poulpe-native-open` with `{question, session}`, UUIDs: the page opens
//   the question. The phone's layout draws it at `/q/<question>`; the full
//   board, which has no such route, at its session's page.
// The page stays the only API client: the shell sends nothing to the server.
import type { PhoneRegistration } from '../api/types'
import { isPhoneWidth, LAYOUT_KEY } from '../phone/usePhone'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

export interface NativePushEnv {
  /** The signed-in person's controls, captured when the event arrives. */
  notifications: () => { registerPhone(args: PhoneRegistration): Promise<unknown>; forgetPhone(): Promise<void> }
  navigate: (path: string) => void
  /** Whether the phone's own screens are drawn now, rather than the full board. */
  phoneLayout: () => boolean
}

/** Whether the phone's layout is in force: a touch phone, and not switched to the full board. */
export function phoneLayoutNow(): boolean {
  let full = false
  try {
    full = localStorage.getItem(LAYOUT_KEY) === 'full'
  } catch {
    full = false
  }
  return isPhoneWidth() && !full
}

/** Where a tap on a notice goes, in the layout in force; null for anything but UUIDs. */
export function openPath(detail: unknown, phone: boolean): string | null {
  if (typeof detail !== 'object' || detail === null) return null
  const { question, session } = detail as { question?: unknown; session?: unknown }
  if (typeof session !== 'string' || !UUID.test(session)) return null
  if (question !== null && (typeof question !== 'string' || !UUID.test(question))) return null
  return phone && question ? `/q/${question}` : `/sessions/${session}`
}

function registration(detail: unknown): PhoneRegistration | 'forget' | null {
  if (typeof detail !== 'object' || detail === null) return null
  const d = detail as Record<string, unknown>
  if (d.forget === true && Object.keys(d).length === 1) return 'forget'
  const { token, environment, app } = d
  if (typeof token !== 'string' || !/^([0-9a-f]{2}){32,100}$/.test(token)) return null
  if (environment !== 'sandbox' && environment !== 'production') return null
  if (typeof app !== 'string' || app.length === 0 || app.length > 155) return null
  return { token, environment, app }
}

/** Follows the shell's events on `target`; answers how to stop. */
export function listenForNativePush(target: EventTarget, env: NativePushEnv): () => void {
  const push = (event: Event) => {
    const asked = registration((event as CustomEvent).detail)
    if (!asked) return
    const controls = env.notifications()
    const done = asked === 'forget' ? controls.forgetPhone() : controls.registerPhone(asked)
    // The server says why it took no phone (no APNs key, say); the board goes on.
    done.catch((error: unknown) => console.warn('iPhone notifications:', error instanceof Error ? error.message : error))
  }
  const open = (event: Event) => {
    const path = openPath((event as CustomEvent).detail, env.phoneLayout())
    if (path) env.navigate(path)
  }
  target.addEventListener('poulpe-native-push', push)
  target.addEventListener('poulpe-native-open', open)
  return () => {
    target.removeEventListener('poulpe-native-push', push)
    target.removeEventListener('poulpe-native-open', open)
  }
}
